import { cn } from "@/lib/cn";
import {
  FloatingFocusManager,
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  type ReferenceType,
} from "@floating-ui/react";
import { useAppDispatch, useAppSelector } from "@store";
import { setVersionDataValue } from "@store/story";
import { getVersionData } from "@store/story/selectors";
import { getVariableValue } from "@/shared/utils/resolve-tokens";
import * as React from "react";
import Button from "./button";
import { Calendar } from "./calendar";
import Input from "./input";
import {
  fromDateValue,
  normalizeVariableOptions,
  toDateValue,
} from "@/dialogs/share-dialog/components/versions/create-or-edit-version/variables/control-utils";
import { getVariableControlKind } from "@/dialogs/share-dialog/components/versions/create-or-edit-version/variables/control-registry";
import { ChoiceControl } from "@/dialogs/share-dialog/components/versions/create-or-edit-version/variables/controls/choice-control";
import { ColorControl } from "@/dialogs/share-dialog/components/versions/create-or-edit-version/variables/controls/color-control";
import { ScalarControl } from "@/dialogs/share-dialog/components/versions/create-or-edit-version/variables/controls/scalar-control";
import { useFastVarDefinitions } from "@/dialogs/share-dialog/components/versions/create-or-edit-version/variables/repeatable/use-fast-var-definitions";
import { track } from "@/features/analytics";
import { VARIABLES_EVENTS } from "@features/dynamic-variables/analytics/variables-analytics";

export interface SingleVariablePopoverProps {
  anchorEl: HTMLElement | null;
  resolveAnchorEl?: () => HTMLElement | null;
  /**
   * Optional rect snapshot captured at the moment of click. Used only as a
   * fallback when React reconciliation detaches the anchor DOM node before
   * Floating UI's first measurement.
   */
  anchorRect?: DOMRect | null;
  variableName: string | null;
  onClose: () => void;
  /**
   * Render an auto-growing multi-line textarea instead of a single-line
   * input. Used by the rich-text editors where variable values can be long
   * and should wrap. The textarea grows with its content up to
   * `TEXTAREA_MAX_HEIGHT`, then scrolls.
   */
  multiline?: boolean;
  /** "date": a calendar picks the value (stored as YYYY-MM-DD) instead of
   * free text — for date variables in version mode.
   *
   * "text" does NOT mean "always a text box": the pill hosts can't tell a
   * `select` from an `input` (they only know the token), so for "text" the
   * popover looks the variable's own org definition up by name and renders
   * the same control the share dialog would give it — see `controlKind`. */
  valueType?: "text" | "date";
}

/** The draft value: a string for every control except multiselect. */
type DraftValue = string | string[];

const sameValue = (a: DraftValue, b: DraftValue): boolean => {
  if (Array.isArray(a) && Array.isArray(b))
    return a.length === b.length && a.every((item, i) => item === b[i]);
  return a === b;
};

/**
 * Controls the popover renders instead of a text box when the variable's org
 * definition says so. Deliberately a SUBSET of the share dialog's kinds:
 *
 *  - `date` is excluded because the hosts already decide it (`valueType`), and
 *    a text-typed pill whose name merely looks like a date must keep the text
 *    box it has always had;
 *  - media / video / iframe / data / list / ai / rich-text need an uploader, a
 *    sheet or a generation round-trip — far more than this 260px popover, and
 *    those variables don't render as inline text pills in the first place.
 */
type PopoverControlKind = "select" | "multiselect" | "number" | "color";

const POPOVER_CONTROL_KINDS = new Set<string>([
  "select",
  "multiselect",
  "number",
  "color",
]);

const TEXTAREA_MAX_HEIGHT = 250;

export const SingleVariablePopover = ({
  anchorEl,
  resolveAnchorEl,
  anchorRect: anchorRectProp,
  variableName,
  onClose,
  multiline = false,
  valueType = "text",
}: SingleVariablePopoverProps) => {
  const open = !!(anchorEl || anchorRectProp) && !!variableName;

  const dispatch = useAppDispatch();
  const versionData = useAppSelector(getVersionData) as
    | Record<string, unknown>
    | undefined;

  const currentValue = React.useMemo<DraftValue>(() => {
    if (!variableName) return "";
    const raw = getVariableValue(versionData, variableName);
    // A multiselect stores a string[] (the shape the share dialog writes);
    // keep it as an array so the choice control can round-trip it.
    if (Array.isArray(raw))
      return raw.map((item) => (item == null ? "" : String(item)));
    return raw == null ? "" : String(raw);
  }, [versionData, variableName]);

  const [draft, setDraft] = React.useState<DraftValue>(currentValue);

  React.useEffect(() => {
    setDraft(currentValue);
  }, [currentValue, variableName]);

  // The org's variable definitions (shared RTK cache with the picker). The
  // pill hosts pass only the token's name, so the type — and a select's
  // options — have to be looked up here.
  const definitions = useFastVarDefinitions();
  const definition = variableName ? definitions.get(variableName) : undefined;
  const controlKind = React.useMemo<PopoverControlKind | null>(() => {
    if (valueType !== "text" || !definition) return null;
    const kind = getVariableControlKind(
      definition.type,
      variableName ?? undefined,
    );
    return POPOVER_CONTROL_KINDS.has(kind)
      ? (kind as PopoverControlKind)
      : null;
  }, [definition, valueType, variableName]);

  const options = React.useMemo(
    () =>
      controlKind === "select" || controlKind === "multiselect"
        ? normalizeVariableOptions(definition?.options)
        : [],
    [controlKind, definition?.options],
  );

  // A select with no options configured would render an empty dropdown the
  // sender can't fill — fall back to the text box (same guard as the
  // repeatable row cells).
  const effectiveKind: PopoverControlKind | null =
    (controlKind === "select" || controlKind === "multiselect") &&
    options.length === 0
      ? null
      : controlKind;

  const textDraft = typeof draft === "string" ? draft : "";

  // Auto-grow the textarea: reset to `auto` to measure the natural content
  // height, then clamp to the max. Past the cap the textarea scrolls.
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  React.useLayoutEffect(() => {
    if (!multiline) return;
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, TEXTAREA_MAX_HEIGHT)}px`;
  }, [draft, multiline]);

  const reference = React.useMemo<Element | null>(() => {
    const currentAnchor = resolveAnchorEl?.() ?? anchorEl;
    if (currentAnchor?.isConnected) {
      const rect = currentAnchor.getBoundingClientRect();
      if (rect.width > 0 || rect.height > 0) return currentAnchor;
    }

    return null;
  }, [anchorEl, resolveAnchorEl]);

  const { refs, floatingStyles, context, update, isPositioned } = useFloating({
    open,
    onOpenChange: (next) => {
      if (!next) onClose();
    },
    // Prefer above the pill; flip to bottom-start when there isn't enough
    // room above.
    placement: "top-start",
    strategy: "fixed",
    middleware: [
      offset(8),
      flip({ fallbackPlacements: ["bottom-start"], padding: 8 }),
      shift({ padding: 8 }),
    ],
  });

  // Use the real DOM anchor whenever it is measurable so Floating UI can track
  // scroll/resize through autoUpdate. The rect fallback only covers a detached
  // contenteditable pill before the first measurement.
  const [fallbackRect, setFallbackRect] = React.useState<DOMRect | null>(null);
  React.useLayoutEffect(() => {
    if (!anchorEl) {
      setFallbackRect(null);
      return;
    }
    setFallbackRect(anchorEl.getBoundingClientRect());
  }, [anchorEl]);

  const positionReference = React.useMemo<ReferenceType | null>(() => {
    const stableRect = anchorRectProp ?? fallbackRect;
    if (!reference && !stableRect) return null;

    return {
      getBoundingClientRect: () => {
        const currentAnchor = resolveAnchorEl?.() ?? anchorEl;
        if (currentAnchor?.isConnected) {
          const live = currentAnchor.getBoundingClientRect();
          if (live.width > 0 || live.height > 0) return live;
        }

        return stableRect ?? new DOMRect(0, 0, 0, 0);
      },
      contextElement: anchorEl ?? undefined,
    };
  }, [anchorEl, anchorRectProp, fallbackRect, reference, resolveAnchorEl]);

  React.useLayoutEffect(() => {
    if (!positionReference) {
      refs.setReference(null);
      return;
    }

    refs.setReference(reference ?? positionReference);
    refs.setPositionReference(positionReference);
    update();
  }, [positionReference, reference, refs, update]);

  React.useLayoutEffect(() => {
    const floating = refs.floating.current;
    if (!open || !positionReference || !floating) return;

    return autoUpdate(reference ?? positionReference, floating, update, {
      animationFrame: true,
    });
  }, [open, positionReference, reference, refs.floating, update]);

  const dismiss = useDismiss(context, { outsidePressEvent: "mousedown" });
  const role = useRole(context);
  const { getFloatingProps } = useInteractions([dismiss, role]);

  const handleSave = () => {
    if (!variableName) return;
    if (!sameValue(draft, currentValue)) {
      dispatch(setVersionDataValue({ key: variableName, value: draft }));
      track(VARIABLES_EVENTS.EDIT_VARIABLE_VALUE, {
        "variable type":
          valueType === "date" ? "date" : String(definition?.type ?? "input"),
      });
    }
    onClose();
  };

  // Collapse newline runs to a single space. A variable value is substituted
  // verbatim into the serialized `window.story` JSON embedded in the published
  // page's inline `<script>`; a raw newline there produces an unterminated
  // string and breaks the reader, so hard line breaks are disallowed.
  const handleDraftChange = (value: string) =>
    setDraft(value.replace(/[\r\n]+/g, " "));

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    // Enter always saves (no newline insertion, including Shift+Enter); the
    // textarea still wraps long values.
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  if (!open) return null;

  return (
    <FloatingPortal>
      <FloatingFocusManager
        context={context}
        modal={false}
        // The text/number inputs carry `autoFocus`, which the focus manager
        // then leaves alone. The select/colour controls have no input to
        // focus, so point at their own trigger (index 0) instead of landing
        // on "Cancel" — where Enter would discard the edit.
        initialFocus={
          effectiveKind === "select" ||
          effectiveKind === "multiselect" ||
          effectiveKind === "color"
            ? 0
            : 1
        }
      >
        <div
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            visibility: isPositioned ? "visible" : "hidden",
          }}
          {...getFloatingProps()}
          className={cn(
            "tw-z-[999999] tw-rounded-xl tw-outline-1 tw-outline tw-outline-blue/5",
            "tw-bg-white tw-shadow-xs tw-p-4 tw-flex tw-flex-col tw-gap-3 tw-min-w-[260px]",
          )}
        >
          <span className="tw-text-sm tw-truncate tw-max-w-56 tw-font-medium tw-leading-5 tw-text-blue-base">
            {variableName}
          </span>
          {valueType === "date" ? (
            <Calendar
              plain
              mode="single"
              selected={fromDateValue(textDraft)}
              defaultMonth={fromDateValue(textDraft) ?? new Date()}
              onSelect={(date) => {
                if (date) setDraft(toDateValue(date));
              }}
            />
          ) : effectiveKind === "select" || effectiveKind === "multiselect" ? (
            <ChoiceControl
              multiple={effectiveKind === "multiselect"}
              options={options}
              value={
                effectiveKind === "multiselect"
                  ? Array.isArray(draft)
                    ? draft
                    : []
                  : textDraft
              }
              onChange={setDraft}
            />
          ) : effectiveKind === "color" ? (
            <ColorControl value={textDraft} onChange={setDraft} />
          ) : effectiveKind === "number" ? (
            <ScalarControl
              autoFocus
              type="number"
              value={textDraft}
              onChange={setDraft}
              onKeyDown={handleKeyDown}
            />
          ) : multiline ? (
            <textarea
              ref={textareaRef}
              autoFocus
              rows={1}
              value={textDraft}
              onChange={(e) => handleDraftChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={variableName ?? ""}
              style={{ maxHeight: TEXTAREA_MAX_HEIGHT }}
              className={cn(
                "scrollbar tw-block tw-w-full tw-resize-none tw-overflow-y-auto",
                "tw-rounded-lg tw-outline tw-outline-1 tw-outline-blue/5 tw-bg-white",
                "tw-px-3 tw-py-2 tw-font-sans tw-text-sm tw-font-normal tw-text-blue-base",
                "placeholder:tw-text-blue-500 focus:tw-outline-violet-700",
              )}
            />
          ) : (
            <Input
              autoFocus
              size="default"
              value={textDraft}
              onChange={(e) => handleDraftChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={variableName ?? ""}
            />
          )}
          <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="default" size="sm" onClick={handleSave}>
              Save changes
            </Button>
          </div>
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  );
};

export default SingleVariablePopover;
